import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { debounce } from 'lodash';

import {
  setFilter,
  unsetFilter
} from 'app/actions/filters';

// filter classes
export const TEXT = 'text';
export const NUMBER = 'number';
export const DATE = 'date';

// filter types
export const CONTAINS = 'contains';
export const NOT_CONTAINS = 'notContains';
export const EQUALS = 'equals';
export const NOT_EQUAL = 'notEqual';
export const GREATER_THAN = 'greaterThan';
export const GREATER_THAN_OR_EQUAL = 'greaterThanOrEqual';
export const LESS_THAN = 'lessThan';
export const LESS_THAN_OR_EQUAL = 'lessThanOrEqual';

// filter operators
// these constants correspond with the server side values
export const OPERATOR_KEY = '_operator';
export const AND_OPERATOR = 'AND';
export const OR_OPERATOR = 'OR';

const useFilter = props => {
  const {
    // AgGrid filter class (type)
    // - text
    // - number
    // - date
    filterClass,

    // AgGrid filter type
    // -- text types
    // - (~)  contains
    // - (!~) notContains
    // - (=)  equals
    // - (!=) notEqual
    // - (^)  startsWith
    // - ($)  endsWith
    // - (_)  blank
    // - (!_) notBlank
    // -- number types
    // - (>)  greaterThan
    // - (>=) greaterThanOrEqual
    // - (<)  lessThan
    // - (<=) lessThanOrEqual
    // - (=)  equals
    // - (!=) notEqual
    // - (_)  blank
    // - (!_) notBlank
    // - (X)  inRange (not used, combine greaterThan and lessThan)
    // -- date types
    // - (>)  greaterThan
    // - (<)  lessThan
    // - (=)  equals
    // - (!=) notEqual
    // - (_)  blank
    // - (!_) notBlank
    // - (X)  inRange (not used, combine greaterThan and lessThan)
    filterType,

    // filterParams passed by AgGrid
    filterParams: {
      colDef: {
        field,
        floatingFilterComponentParams: {
          resource
        }
      }
    },

    // format the value from onChange (different for each component)
    valueFormatter,

    // filter index assigned by FilterStack
    filterIndex
  } = props;

  // get filterModel from redux when it updates
  const reduxParams = useSelector(state => state.params[resource]);
  const filterModel = reduxParams?.filter_model;
  const filter = (filterModel[field] && filterModel[field][filterIndex]) || {};

  // track filterType in state, only update Redux with values
  // (otherwise the grid will refresh)
  const [state, setState] = useState({
    filterType: filter?.filterType || filterType || CONTAINS
  });

  const dispatch = useDispatch();

  // the default operator for the first filter should be OR,
  // and for the subsequent filters it should be AND
  // (the subsequent filters can be changed via AND/OR buttons,
  // while the first filter can only be changed via code,
  // which is used by functions like searchCustomers
  // to search for customers under a job where (this or that)
  const defaultOperator = filterIndex === 0 ? OR_OPERATOR : AND_OPERATOR;

  const updateFilter = value => {
    dispatch(setFilter({
      resource,
      field,
      filterIndex,
      filterClass,
      filterType: state.filterType,
      value,
      operator: filter.operator || defaultOperator
    }));
  }

  const setValue = debounce(value => {
    if (!value) {
      clearValue();
      return;
    }

    if (value === filter.value) {
      return;
    }

    updateFilter(value);
  }, 1000);

  const clearValue = () => {
    // clear the filter
    dispatch(unsetFilter({
      resource,
      field,
      filterIndex
    }));
  }

  const setFilterType = filterType => {
    setState(state => ({ ...state, filterType }));
  }

  // this callback fires when the input changes (keypress, etc.)
  const onChange = (event, option = null) => {
    const value = valueFormatter(event, option);
    setValue(value);
  };

  // provide handle for component to refresh after changing filter type
  const refresh = () => {
    const event = { target: filter.value };
    onChange(event);
  }

  return {
    value: filter.value,
    clearValue,
    filterType: state.filterType,
    setFilterType,
    onChange,
    refresh
  }
}

export default useFilter;
